import { useMutation, useQuery } from '@apollo/client';
import { datadogLogs } from '@datadog/browser-logs';
import {
  AlertBannerInteractionTransitions,
  AlertBannerPlatform,
  type AlertBannerTransitionAlertBannerInteractionMutationVariables,
  PersistenceBehaviour,
} from '@generated/graphql';
import { UserContext } from '@providers/userContextProvider';
import { safeWindowOpen } from '@utils/urlSanitization';
import {
  AlertBanner as AlertBannerBar,
  Button,
  useToast,
} from 'hephaestus/web';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import {
  type AlertBannerInteractionItem,
  FETCH_VISIBLE_ALERT_BANNERS,
  TRANSITION_ALERT_BANNER_INTERACTION,
  UPSERT_ALERT_BANNER_INTERACTIONS,
} from './alertBanner.query';
import {
  BANNER_SEVERITY,
  sortBannerInteractionsByType,
} from './alertBanner.util';

// Error link reads skipErrorHandler from variables. Exact<> rejects it as a schema field.
const transitionVariables = (
  alertBannerInteractionId: string,
  transition: AlertBannerInteractionTransitions,
): AlertBannerTransitionAlertBannerInteractionMutationVariables =>
  ({
    input: { alertBannerInteractionId, transition },
    skipErrorHandler: true,
  }) as AlertBannerTransitionAlertBannerInteractionMutationVariables;

export const AlertBanner = () => {
  const { t } = useTranslation(['alertBanners', 'shared']);
  const { translateBackendString } = useLocalization();
  const { enqueueToast } = useToast();
  const { activeOrganization } = useContext(UserContext);
  const [bannerInteractions, setBannerInteractions] = useState<
    AlertBannerInteractionItem[]
  >([]);
  const [refetchIndex, setRefreshIndex] = useState(0);

  const organizationId = activeOrganization?.id;

  const { loading, data, refetch } = useQuery(FETCH_VISIBLE_ALERT_BANNERS, {
    skip: !organizationId,
    variables: {
      input: {
        organizationId,
        platform: AlertBannerPlatform.Web,
      },
    },
    fetchPolicy: 'network-only',
  });

  const alertBannerIds = useMemo(
    () =>
      data?.fetchVisibleAlertBanners.visibleAlertBanners.map(
        (visibleBanner) => visibleBanner.id,
      ) ?? [],
    [data],
  );

  const [
    upsertAlertBannerInteractions,
    { loading: upsertInteractionsLoading },
  ] = useMutation(UPSERT_ALERT_BANNER_INTERACTIONS, {
    fetchPolicy: 'no-cache',
    onCompleted: (upsertData) => {
      setBannerInteractions(
        upsertData.upsertAlertBannerInteractions.alertBannerInteractions,
      );
    },
  });

  const [transitionAlertBannerInteraction] = useMutation(
    TRANSITION_ALERT_BANNER_INTERACTION,
  );

  useEffect(() => {
    // refetchIndex is read so a failed transition upserts again with the same ids
    if (refetchIndex < 0) return;

    if (!organizationId || alertBannerIds.length === 0) {
      setBannerInteractions([]);
      return;
    }

    void upsertAlertBannerInteractions({
      variables: {
        input: {
          alertBannerIds,
          organizationId,
        },
      },
    });
  }, [
    upsertAlertBannerInteractions,
    alertBannerIds,
    organizationId,
    refetchIndex,
  ]);

  const removeBanner = (bannerInteraction: AlertBannerInteractionItem) => {
    setBannerInteractions((prev) =>
      prev.filter((interaction) => interaction.id !== bannerInteraction.id),
    );
  };

  const refetchVisibleBanners = async () => {
    await refetch();
    setRefreshIndex((idx) => idx + 1);
  };

  const handleAction = async (
    bannerInteraction: AlertBannerInteractionItem,
  ) => {
    if (!bannerInteraction.alertBanner.configuration?.enableAction) return;

    try {
      await transitionAlertBannerInteraction({
        variables: transitionVariables(
          bannerInteraction.id,
          AlertBannerInteractionTransitions.Action,
        ),
      });
      safeWindowOpen(bannerInteraction.alertBanner.actionButton?.url);

      if (
        bannerInteraction.alertBanner.persistenceBehaviour ===
        PersistenceBehaviour.UntilActioned
      ) {
        removeBanner(bannerInteraction);
      }
    } catch (e: unknown) {
      datadogLogs.logger.error('Alert banner action transition failed', {
        alertBannerInteractionId: bannerInteraction.id,
        error: e instanceof Error ? e.message : String(e),
      });
      enqueueToast(t('transitionError'), { variant: 'error' });
      removeBanner(bannerInteraction);
      await refetchVisibleBanners();
    }
  };

  const handleDismiss = async (
    bannerInteraction: AlertBannerInteractionItem,
  ) => {
    if (!bannerInteraction.alertBanner.configuration?.makeDismissible) return;

    try {
      await transitionAlertBannerInteraction({
        variables: transitionVariables(
          bannerInteraction.id,
          AlertBannerInteractionTransitions.Dismiss,
        ),
      });
    } catch (e: unknown) {
      datadogLogs.logger.error('Alert banner dismiss transition failed', {
        alertBannerInteractionId: bannerInteraction.id,
        error: e instanceof Error ? e.message : String(e),
      });
      enqueueToast(t('transitionError'), { variant: 'error' });
      await refetchVisibleBanners();
    }

    removeBanner(bannerInteraction);
  };

  const sortedBannerInteractions =
    sortBannerInteractionsByType(bannerInteractions);

  if (
    loading ||
    upsertInteractionsLoading ||
    sortedBannerInteractions.length === 0
  )
    return null;

  return (
    <>
      {sortedBannerInteractions.map((interaction) => {
        const banner = interaction.alertBanner;
        const severity = banner.bannerType
          ? BANNER_SEVERITY[banner.bannerType]
          : undefined;
        if (!severity) return null;

        const actionLabel = banner.actionButton?.label || '';
        const showAction =
          (banner.configuration?.enableAction ?? false) && Boolean(actionLabel);

        return (
          <AlertBannerBar
            key={interaction.id}
            severity={severity}
            closeText={t('shared:close')}
            onClose={
              banner.configuration?.makeDismissible
                ? () => handleDismiss(interaction)
                : undefined
            }
            action={
              showAction ? (
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => handleAction(interaction)}
                >
                  {actionLabel}
                </Button>
              ) : undefined
            }
            sx={{ flex: 1 }}
          >
            {translateBackendString(banner.bannerCopy)}
          </AlertBannerBar>
        );
      })}
    </>
  );
};
